import { useMemo } from 'react'

import { useLocale } from '@rewardops/ui'

import { useApi } from '../use-api'

interface Category {
  id: string
  key: string
  name: string
  code: string
  subCategories: { id: string; code: string }[]
}

interface UseMainCategoriesFilterOptions {
  /**
   * Enable or disable fetching categories.
   */
  enabled?: boolean
  /**
   * List of subcategory codes to filter main categories by.
   * If empty or not provided, all main categories will be returned.
   */
  filter?: {
    subCategories?: string[]
  }
}

export const useRootCategoriesFilter = (
  options: UseMainCategoriesFilterOptions = {},
) => {
  const { enabled = false, filter: { subCategories = [] } = {} } = options
  const { api } = useApi()
  const { locale } = useLocale()

  const { data: categoriesData, isLoading } =
    api.categories.getSummarized.useQuery(undefined, {
      enabled,
      cacheTime: 0,
      staleTime: 0,
    })

  const loading = enabled && isLoading

  const normalizeRootCategories = (categories: any[]): Category[] => {
    return categories
      .filter((category) => category?.mainCategory)
      .map((category) => {
        const { id, name, code, subCategories } = category.mainCategory
        return {
          id,
          name: name[locale] ?? Object.values(name)[0],
          code,
          key: id,
          subCategories: subCategories?.map((scat: any) => scat.code) || [],
        }
      })
  }

  const filterCategoriesBySubcategories = (
    mainCategories: Category[],
    filterBySubcategories: string[],
  ): Category[] => {
    const subcategoryToCategoryMap = mainCategories.reduce((acc, category) => {
      category.subCategories.forEach(
        (code) => (acc[code as any] = category.code),
      )
      return acc
    }, {} as Record<string, string>)

    const mainCategoryCodes = Array.from(
      new Set(
        filterBySubcategories.map(
          (code) => subcategoryToCategoryMap[code as any] ?? code,
        ),
      ),
    )

    return mainCategories.filter((category) =>
      mainCategoryCodes.includes(category.code),
    )
  }

  const categories = useMemo(() => {
    if (!enabled) {
      return []
    }

    const rootCategories = normalizeRootCategories(
      (categoriesData as any) || [],
    )

    if (subCategories.length === 0) {
      return rootCategories // Return all categories if no filter is applied
    }

    return filterCategoriesBySubcategories(rootCategories, subCategories as any)
  }, [categoriesData, subCategories, locale, enabled])

  return { categories, loading }
}
